import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import type { EventPlacesQuery } from './eventPlace.graphql.generated';
import { EventPlacesDocument } from './eventPlace.graphql.generated';
import type { EventPlace } from './EventPlace.types';
import { useEventInfo } from '../eventInfo';

/**
 * Interface for the update cache parameters used in adding, updating, or removing event places.
 */
export interface UpdateEventPlaceCacheArgs {
  cache?: ApolloCache<{}>;
  eventPlace?: EventPlace;
  pointOnMapId?: string | null;
  eventHandle?: string;
  action: 'add' | 'update' | 'remove';
}

/**
 * Function that updates the Apollo cache after adding, updating, or removing event places or points on the map.
 * @param {UpdateEventPlaceCacheArgs} props - The parameters used to update the cache.
 */
export const useEventPlaceCacheUpdate = () => {
  const apolloClient = useApolloClient();
  const { eventInfo } = useEventInfo();
  const updateEventPlaceCache = ({ cache, eventPlace, pointOnMapId, eventHandle, action }: UpdateEventPlaceCacheArgs) => {
    const cacheClient = cache ?? apolloClient;
    const queryParam = {
      query: EventPlacesDocument,
      variables: {
        name: eventHandle!,
        payload: { eventName: eventHandle!, pageSlug: 'accommodations' },
        isAdmin: true,
      },
      batchMode: 'fast',
    };

    const cachedData = cacheClient.readQuery<EventPlacesQuery>(queryParam);
    const cachedPlaces = cachedData?.eventByName?.places ?? [];
    const cachedComponents = cachedData?.pageContainerBySlug?.components;

    // Update places based on the action
    let places;
    if (eventPlace) {
      if (action === 'add') {
        places = pointOnMapId ? [eventPlace] : [...cachedPlaces, eventPlace];
      } else if (action === 'update') {
        places = [...cachedPlaces.filter(place => place.id !== eventPlace?.id), eventPlace];
      } else {
        places = cachedPlaces.filter(place => place.id !== eventPlace?.id);
      }
    } else {
      places = pointOnMapId ? [] : cachedPlaces;
    }

    let components = cachedComponents;

    // Update travel map components if removing a point on the map
    if (pointOnMapId) {
      const findTravelMapComponent = cachedComponents?.find(component => component.__typename === 'TravelMapComponent');
      const travelMapComponent = findTravelMapComponent?.__typename === 'TravelMapComponent' ? findTravelMapComponent : undefined;
      if (travelMapComponent) {
        const updatedPointsOnMap = travelMapComponent.pointsOnMap?.filter(point => point?.id !== pointOnMapId);
        const updatedTravelMapComponent = { ...travelMapComponent, pointsOnMap: updatedPointsOnMap };
        components = cachedComponents ? [...(cachedComponents.filter(component => component.__typename !== 'TravelMapComponent') ?? []), updatedTravelMapComponent] : [];
      }
    }

    // Write the updated data back to the Apollo cache
    const eventId = cachedData?.eventByName?.id || eventInfo?.eventId;
    cacheClient.writeQuery<EventPlacesQuery>({
      ...queryParam,
      data: {
        ...cachedData,
        ...(eventId && {
          eventByName: {
            ...cachedData?.eventByName,
            __typename: 'Event',
            id: eventId,
            places,
          },
        }),
        ...(cachedData?.pageContainerBySlug?.id && {
          pageContainerBySlug: {
            ...cachedData.pageContainerBySlug,
            __typename: 'PageContainer',
            id: cachedData.pageContainerBySlug.id,
            components: components ?? [],
          },
        }),
      },
    });
  };
  return { updateEventPlaceCache };
};
